import { ReactNode, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import api from "services/api";
import { errorMessageOf } from "v2/utils/errorMessageOf";
import { PoweredByFooter } from "v2/pages/PlanExtensionFormulary/components/PoweredByFooter";

type Resolved = {
  type: string;
  holder: { name: string; taxPayerId: string };
  dependents: { taxPayerId: string; name: string; birthDate: string | null }[];
  question: string;
  expirationDate: string | null;
  /** Spec 2 §6.2.1: ADENDO pergunta só pelos `dependents` pendentes; `locked` já vale e é só leitura. */
  mode?: "FULL" | "ADENDO";
  locked?: { taxPayerId: string; name: string; beneficiaryType: "TITULAR" | "DEPENDENT"; isOptant: boolean }[];
  /** Logo da empregadora; sem ele, só o rodapé. */
  logoEmpresa?: string | null;
};

/** O colaborador vê a empregadora no topo e "Powered by Conducco" no rodapé. */
function ComMarca({ logo, children }: { logo?: string | null; children: ReactNode }) {
  return (
    <Box p={4} maxWidth={560} mx="auto">
      {logo && <Box component="img" src={logo} alt="Logo da empresa" sx={{ height: 56, width: "auto", mb: 3, display: "block" }} />}
      {children}
      <Box mt={6}><PoweredByFooter /></Box>
    </Box>
  );
}

/**
 * Formulário público do app de campanhas (spec A; primeiro uso, spec 2).
 * O titular responde por si e pelos dependentes num formulário só; a
 * plataforma grava uma decisão por CPF. Titular "não" leva todos junto;
 * titular "sim" com dependente desmarcado grava "não" só para o dependente.
 */
export function CampaignDecisionForm() {
  const { organizationId, token } = useParams() as { organizationId: string; token: string };
  const [data, setData] = useState<Resolved | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "invalid" | "unavailable" | "done" | "sending">("loading");
  const [holderYes, setHolderYes] = useState<boolean | null>(null);
  const [deps, setDeps] = useState<Record<string, boolean>>({});
  /** Adendo: cada pendente precisa de um sim ou não explícito; silêncio não é resposta. */
  const [pending, setPending] = useState<Record<string, boolean | undefined>>({});
  const [reason, setReason] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setState("loading");
    api
      .get(`/v2/campanha/${organizationId}/${token}/resolve`)
      .then(({ data: body }) => {
        const resolved = body.data as Resolved;
        setData(resolved);
        setDeps(Object.fromEntries((resolved.dependents ?? []).map((d) => [d.taxPayerId, true])));
        setState("ready");
      })
      /**
       * Só o 404 do backend quer dizer "respondida, recusada, vencida ou fechada". Um 500,
       * um 502 do proxy ou queda de rede não podem dizer ao beneficiário que o link expirou:
       * ele não volta, e a inclusão fica em espera sem prazo (revisão 20/09, F6).
       */
      .catch((error) => setState(error?.response?.status === 404 ? "invalid" : "unavailable"));
  }, [organizationId, token, attempt]);

  /**
   * CT-F3 (spec contributários §6.2): conta quando a página mostrou o formulário, não quando o
   * link foi pedido; leitores de link de e-mail corporativo abrem links sem pessoa. Uma chamada
   * por link nesta montagem: nova tentativa do resolve e volta a "ready" depois de envio recusado
   * não contam de novo. Sem espera e sem erro na tela: a medição nunca atrapalha a resposta.
   */
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    const link = `${organizationId}/${token}`;
    if (state !== "ready" || openedFor.current === link) return;
    openedFor.current = link;
    api.post(`/v2/campanha/${organizationId}/${token}/opened`).catch(() => undefined);
  }, [state, organizationId, token]);

  const isAdendo = data?.mode === "ADENDO";
  const adendoComplete = !!data && data.dependents.every((d) => typeof pending[d.taxPayerId] === "boolean");

  async function submit() {
    if (!data) return;
    if (isAdendo ? !adendoComplete : holderYes === null) return;
    setState("sending");
    const decisions = isAdendo
      ? data.dependents.map((d) => ({ taxPayerId: d.taxPayerId, isOptant: pending[d.taxPayerId] === true }))
      : [
          { taxPayerId: data.holder.taxPayerId, isOptant: holderYes === true },
          ...data.dependents.map((d) => ({ taxPayerId: d.taxPayerId, isOptant: holderYes === true && !!deps[d.taxPayerId] })),
        ];
    setSubmitError(null);
    try {
      await api.post(`/v2/campanha/${organizationId}/${token}/submit`, { decisions, reason: reason || undefined });
      setState("done");
    } catch (error: any) {
      /** Voltar ao formulário em silêncio faria o beneficiário achar que enviou (F7). */
      if (error?.response?.status === 404) {
        setState("invalid");
        return;
      }
      setSubmitError(errorMessageOf(error, "Não conseguimos registrar sua resposta. Tente de novo em instantes."));
      setState("ready");
    }
  }

  if (state === "loading") return <Box p={4}><CircularProgress /></Box>;
  if (state === "invalid") return <ComMarca><Typography>Este link não está mais disponível.</Typography></ComMarca>;
  if (state === "unavailable") {
    return (
      <ComMarca>
        <Typography gutterBottom>Não conseguimos carregar o formulário. Tente de novo em instantes.</Typography>
        <Button variant="outlined" onClick={() => setAttempt((n) => n + 1)}>Tentar de novo</Button>
      </ComMarca>
    );
  }
  if (!data) return <Box p={4}><CircularProgress /></Box>;
  if (state === "done") return <ComMarca logo={data.logoEmpresa}><Typography variant="h6">Resposta registrada. Obrigado.</Typography></ComMarca>;

  if (isAdendo) {
    return (
      <ComMarca logo={data.logoEmpresa}>
        <Typography variant="h5" gutterBottom>{data.holder.name ? `Olá, ${data.holder.name}` : "Olá"}</Typography>
        <Typography gutterBottom>{data.question}</Typography>
        {(data.locked ?? []).length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2">Respostas já registradas</Typography>
            {(data.locked ?? []).map((l) => (
              <Typography key={l.taxPayerId} variant="body2" color="text.secondary">
                {l.name || (l.beneficiaryType === "TITULAR" ? "Titular" : "Dependente")}: {l.isOptant ? "participa" : "não participa"}
              </Typography>
            ))}
          </Box>
        )}
        <Box mt={2}>
          {data.dependents.map((d) => (
            <Box key={d.taxPayerId} mt={1}>
              <Typography variant="subtitle2">{d.name}</Typography>
              <RadioGroup
                row
                value={pending[d.taxPayerId] === undefined ? "" : String(pending[d.taxPayerId])}
                onChange={(e) => setPending({ ...pending, [d.taxPayerId]: e.target.value === "true" })}
              >
                <FormControlLabel value="true" control={<Radio inputProps={{ "aria-label": `${d.name}: sim, incluir` }} />} label="Sim, incluir" />
                <FormControlLabel value="false" control={<Radio inputProps={{ "aria-label": `${d.name}: não incluir` }} />} label="Não incluir" />
              </RadioGroup>
            </Box>
          ))}
        </Box>
        {submitError && (
          <Typography color="error" role="alert" sx={{ mt: 2 }}>{submitError}</Typography>
        )}
        <Button variant="contained" sx={{ mt: 3 }} disabled={!adendoComplete || state === "sending"} onClick={submit}>
          Enviar resposta
        </Button>
      </ComMarca>
    );
  }

  return (
    <ComMarca logo={data.logoEmpresa}>
      <Typography variant="h5" gutterBottom>{data.holder.name ? `Olá, ${data.holder.name}` : "Olá"}</Typography>
      <Typography gutterBottom>{data.question}</Typography>
      <RadioGroup value={holderYes === null ? "" : String(holderYes)} onChange={(e) => setHolderYes(e.target.value === "true")}>
        <FormControlLabel value="true" control={<Radio />} label="Sim, quero participar" />
        <FormControlLabel value="false" control={<Radio />} label="Não quero participar" />
      </RadioGroup>
      {holderYes && data.dependents.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2">Dependentes que também participam</Typography>
          {data.dependents.map((d) => (
            <FormControlLabel
              key={d.taxPayerId}
              control={<Checkbox checked={!!deps[d.taxPayerId]} onChange={(e) => setDeps({ ...deps, [d.taxPayerId]: e.target.checked })} />}
              label={d.name}
            />
          ))}
        </Box>
      )}
      {holderYes === false && (
        <TextField fullWidth margin="normal" label="Motivo (opcional)" value={reason} onChange={(e) => setReason(e.target.value)} />
      )}
      {submitError && (
        <Typography color="error" role="alert" sx={{ mt: 2 }}>{submitError}</Typography>
      )}
      <Button variant="contained" sx={{ mt: 3 }} disabled={holderYes === null || state === "sending"} onClick={submit}>
        Enviar resposta
      </Button>
    </ComMarca>
  );
}
